import React, { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import TextInput from '../../../../web-app-components/components/TextInput';
import SelectInput from '../../../../web-app-components/components/SelectInput';
import useStateThrottled from '../../../../web-app-components/hooks/useStateThrottled';
import makeStyles from '@material-ui/core/styles/makeStyles';
import SearchIcon from '@material-ui/icons/Search';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import { DataGrid } from '@material-ui/data-grid';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';

import objectsSuggestedClient from '../../../../clients/objects-suggested';
import categoriesClient from '../../../../clients/categories';
import subcategoriesClient from '../../../../clients/subcategories';

import FilterSelectInput, { filters } from './components/FilterSelectInput';

const columns = [
  {
    field: 'id',
    headerName: 'Tipas',
    sortable: false,
    renderCell: (params) => {
      if (params?.row?.object) {
        return 'Esamas';
      }
      return 'Naujas';
    },
  },
  {
    field: 'Title',
    headerName: 'Pavadinimas',
    flex: 1,
    sortable: false,
    renderCell: (params) => {
      if (params?.row?.object) {
        return params?.row?.object?.[params?.field];
      }
      return params?.formattedValue;
    },
  },
];

const useStyles = makeStyles((theme) => ({
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1),
    flexWrap: 'wrap',

    '& > :not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },
  searchInput: {
    flex: '0 1 300px',
  },
  rightControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  filterSelect: {
    minWidth: 200,
    maxWidth: 240,
  },
  renderValue: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const ObjectsList = () => {
  const classes = useStyles();

  const history = useHistory();
  const { path } = useRouteMatch();

  const { enqueueSnackbar } = useSnackbar();

  const [filter, setFilter] = useState(filters.ALL);
  const [throttledSearchTerm, searchTerm, setSearchTerm] = useStateThrottled(
    '',
    500,
  );
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);

  const [objects, setObjects] = useState();
  const [categories, setCategories] = useState();
  const [subcategories, setSubcategories] = useState();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Fetch categories on mount
  useEffect(() => {
    (async () => {
      try {
        setCategories(await categoriesClient.find({ _sort: 'Order:asc' }));
      } catch (error) {
        enqueueSnackbar(
          'Nepavyko gauti kategorijų. Bandykite atidaryti iš naujo.',
          { variant: 'error' },
        );
      }
    })();
  }, [enqueueSnackbar]);

  // Fetch subcategories when categories are selected
  useEffect(() => {
    if (!selectedCategories || selectedCategories.length === 0) {
      setSubcategories([]);
      return;
    }

    (async () => {
      try {
        const data = await subcategoriesClient.find({
          filters: {
            category: {
              documentId: {
                $in: selectedCategories,
              },
            },
          },
          sort: 'Order:asc',
        });
        setSubcategories(data);
      } catch (error) {
        enqueueSnackbar(
          'Nepavyko gauti subkategorijų. Bandykite atidaryti iš naujo.',
          { variant: 'error' },
        );
        setSubcategories([]);
      }
    })();
  }, [selectedCategories, enqueueSnackbar]);

  // Clear subcategories when no categories are selected
  useEffect(() => {
    if (!selectedCategories || selectedCategories.length === 0) {
      setSelectedSubcategories([]);
    }
  }, [selectedCategories]);

  // Fetch objects with filters
  useEffect(() => {
    (async () => {
      try {
        const query = { sort: 'createdAt:desc' };
        const filtersObj = {};

        // Handle subcategories filter first since it affects object filter
        // For object-suggested, subcategories need to be filtered through the object relation
        // This only works for items that have an associated object
        if (selectedSubcategories && selectedSubcategories.length > 0) {
          // For NEW items (without object), we can't filter by subcategories
          if (filter === filters.NEW) {
            // Clear the results since NEW items don't have objects with subcategories
            setObjects([]);
            return;
          }
          // $null is only allowed at root level. Use root-level $and for two separate conditions on object.
          filtersObj.$and = [
            { object: { $null: false } },
            {
              object: {
                subcategories: {
                  documentId: {
                    $in: selectedSubcategories,
                  },
                },
              },
            },
          ];
        } else {
          // No subcategory filter, apply normal object filter
          if (filter === filters.NEW) {
            filtersObj.object = { $null: true };
          }
          if (filter === filters.EDIT) {
            filtersObj.object = { $null: false };
          }
        }

        if (throttledSearchTerm.trim()) {
          filtersObj.Title = { $containsi: throttledSearchTerm.trim() };
        }
        if (selectedCategories && selectedCategories.length > 0) {
          filtersObj.categories = {
            documentId: {
              $in: selectedCategories,
            },
          };
        }

        if (Object.keys(filtersObj).length > 0) {
          query.filters = filtersObj;
        }

        setObjects(await objectsSuggestedClient.find(query));
      } catch (error) {
        enqueueSnackbar(
          'Nepavyko gauti objektų sąrašo. Bandykite atidaryti iš naujo.',
          { variant: 'error' },
        );
      }
    })();
  }, [
    filter,
    throttledSearchTerm,
    selectedCategories,
    selectedSubcategories,
    enqueueSnackbar,
  ]);

  const handleFilterChange = ({ target: { value } }) => setFilter(value);

  const handleSearchChange = ({ target: { value } }) => setSearchTerm(value);

  const handleCategoriesChange = ({ target: { value } }) => {
    setSelectedCategories(typeof value === 'string' ? value.split(',') : value);
  };

  const handleSubcategoriesChange = ({ target: { value } }) => {
    setSelectedSubcategories(
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleNewObjectClick = () => history.push(`${path}/new`);

  const handleRowClick = ({ id }) => history.push(`${path}/${id}`);

  const toIds = (arr) =>
    Array.isArray(arr)
      ? arr.map((v) =>
          typeof v === 'object' && v != null && 'id' in v ? v.id : v,
        )
      : [];

  const renderCategoryValue = (selected) => {
    const ids = toIds(selected);
    if (ids.length === 0) return '';
    const categoriesMap = categories
      ? categories.reduce((acc, cat) => {
          acc[cat.id] = cat.Title;
          return acc;
        }, {})
      : {};
    const labels = ids.map((id) => categoriesMap[id] || '').filter(Boolean);
    const text =
      labels.length === 1 ? labels[0] : `${labels.length} pasirinkta`;
    return (
      <span className={classes.renderValue} title={labels.join(', ')}>
        {text}
      </span>
    );
  };

  const renderSubcategoryValue = (selected) => {
    const ids = toIds(selected);
    if (ids.length === 0) return '';
    const subcategoriesMap = subcategories
      ? subcategories.reduce((acc, sub) => {
          acc[sub.id] = sub.Title;
          return acc;
        }, {})
      : {};
    const labels = ids.map((id) => subcategoriesMap[id] || '').filter(Boolean);
    const text =
      labels.length === 1 ? labels[0] : `${labels.length} pasirinkta`;
    return (
      <span className={classes.renderValue} title={labels.join(', ')}>
        {text}
      </span>
    );
  };

  return (
    <div>
      <div className={classes.controls}>
        <TextInput
          className={classes.searchInput}
          placeholder="Ieškoti pagal pavadinimą..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <div className={classes.rightControls}>
          {categories ? (
            <SelectInput
              className={classes.filterSelect}
              label="Kategorijos"
              multiple
              value={selectedCategories}
              onChange={handleCategoriesChange}
              renderValue={renderCategoryValue}
            >
              {categories.map(({ id, Title }) => (
                <MenuItem key={id} value={id}>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedCategories.indexOf(id) !== -1}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primary={Title} />
                </MenuItem>
              ))}
            </SelectInput>
          ) : (
            <CircularProgress size={24} />
          )}
          {selectedCategories &&
            selectedCategories.length > 0 &&
            subcategories &&
            subcategories.length > 0 && (
              <SelectInput
                className={classes.filterSelect}
                label="Subkategorijos"
                multiple
                value={selectedSubcategories}
                onChange={handleSubcategoriesChange}
                renderValue={renderSubcategoryValue}
              >
                {subcategories.map(({ id, Title }) => (
                  <MenuItem key={id} value={id}>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedSubcategories.indexOf(id) !== -1}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText primary={Title} />
                  </MenuItem>
                ))}
              </SelectInput>
            )}
          <FilterSelectInput
            label="Rodinys"
            value={filter}
            onChange={handleFilterChange}
          />
        </div>
      </div>
      <Paper>
        <DataGrid
          columns={columns}
          rows={objects || []}
          loading={!objects}
          onRowClick={handleRowClick}
          autoHeight
          disableColumnMenu
          disableSelectionOnClick
          pagination
          page={page}
          onPageChange={(params) => setPage(params.page)}
          pageSize={pageSize}
          onPageSizeChange={(params) => {
            setPageSize(params.pageSize);
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </div>
  );
};

export default ObjectsList;
